// components/CadenceConfig.tsx
import { useState } from 'react';
import {
  CadenceSource,
  setCadenceSource,
  getCadenceSource,
  connectToCadenceSensor,
  isCadenceConnected,
} from '../lib/bluetooth';

interface CadenceConfigProps {
  onCadenceConnectionChange: (_isConnected: boolean) => void;
}

export function CadenceConfig({
  onCadenceConnectionChange,
}: CadenceConfigProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSource, setSelectedSource] =
    useState<CadenceSource>(getCadenceSource());
  const [isConnecting, setIsConnecting] = useState(false);
  const [cadenceConnected, setCadenceConnected] =
    useState(isCadenceConnected());

  const handleSourceChange = async (source: CadenceSource) => {
    setSelectedSource(source);
    setCadenceSource(source);

    if (source === 'external') {
      try {
        setIsConnecting(true);
        await connectToCadenceSensor();
        setCadenceConnected(true);
        onCadenceConnectionChange(true);
      } catch (e) {
        alert('Failed to connect to cadence sensor: ' + (e as Error).message);
        setSelectedSource('disabled');
        setCadenceSource('disabled');
      } finally {
        setIsConnecting(false);
      }
    } else {
      setCadenceConnected(false);
      onCadenceConnectionChange(source === 'trainer');
    }
  };

  const getSourceDescription = (source: CadenceSource) => {
    switch (source) {
      case 'trainer':
        return 'Get cadence data from your smart trainer (if supported)';
      case 'external':
        return 'Connect to a dedicated cadence sensor';
      case 'disabled':
        return 'No cadence tracking';
    }
  };

  const getConnectionStatus = () => {
    if (selectedSource === 'disabled') return null;
    if (selectedSource === 'trainer') return 'Will use trainer data';
    if (selectedSource === 'external') {
      if (isConnecting) return 'Connecting...';
      return cadenceConnected ? 'Sensor Connected ✅' : 'Not Connected';
    }
  };

  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center justify-center space-x-2"
      >
        <span>⚙️</span>
        <span>Cadence Setup</span>
        <span
          className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          ↓
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-4">
          <h3 className="font-semibold text-gray-800 mb-3">
            Cadence Data Source
          </h3>

          <div className="space-y-3">
            {(['disabled', 'trainer', 'external'] as CadenceSource[]).map(
              (source) => (
                <label
                  key={source}
                  className="flex items-start space-x-3 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="cadenceSource"
                    value={source}
                    checked={selectedSource === source}
                    onChange={() => handleSourceChange(source)}
                    disabled={isConnecting}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-700 capitalize">
                      {source === 'disabled'
                        ? 'No Cadence'
                        : source === 'trainer'
                          ? 'Smart Trainer'
                          : 'External Sensor'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {getSourceDescription(source)}
                    </div>
                    {selectedSource === source && getConnectionStatus() && (
                      <div className="text-sm mt-1 text-blue-600">
                        {getConnectionStatus()}
                      </div>
                    )}
                  </div>
                </label>
              )
            )}
          </div>

          <button
            onClick={() => setIsOpen(false)}
            className="mt-3 w-full px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
